import { useEffect, useRef, useState, type FormEvent } from 'react';
import { v } from '../constants';
import { Wordmark } from './ui';
import { apiFetch, apiJson } from '../lib/api';

// /complete-signup — second leg of the Google/Apple sign-up flow.
//
// Flow: user clicks "Continue with Google/Apple" → /api/v1/auth/oauth/<p>
//       → provider login → callback. If the user is brand-new the
//       callback sets the `hp_oauth_pending` cookie and redirects here
//       with no query state. This page reads no params — the cookie
//       carries the OAuth profile; the user just picks a display name
//       and submits.
//
// The cookie is HttpOnly so we can't read it from JS to pre-fill the
// suggested name. The server exposes the suggestion via
// `GET /api/v1/auth/oauth/pending` (cookie auto-attached), or we can
// just let the user type from scratch — simpler + the typing is fast.

type Availability =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'available' }
  | { kind: 'unavailable'; reason: 'too_short' | 'too_long' | 'invalid_chars' | 'in_use' };

const REASON_COPY: Record<string, string> = {
  too_short: 'At least 3 characters.',
  too_long: 'Max 30 characters.',
  invalid_chars: 'Letters, numbers, and underscores only.',
  in_use: 'That name is taken — try another.',
};

const SUBMIT_ERROR_COPY: Record<string, string> = {
  oauth_session_expired: 'Your sign-in session expired. Head back and try again.',
  displayName_in_use: 'Someone just took that name. Try another.',
  invalid_displayName: 'Display name needs to be 3–30 characters.',
  rate_limited: 'Too many attempts. Try again in a few minutes.',
};

export function CompleteSignupPage() {
  const [name, setName] = useState('');
  const [touched, setTouched] = useState(false);
  const [availability, setAvailability] = useState<Availability>({ kind: 'idle' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    document.body.classList.add('hm-page-scroll');
    return () => { document.body.classList.remove('hm-page-scroll'); };
  }, []);

  // Pre-fill from a tiny endpoint that reads the pending cookie. Best-
  // effort — if it fails (cookie missing/expired), the page still works,
  // user just types from scratch.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await apiJson<{ suggestedName?: string }>('/auth/oauth/pending');
      if (cancelled) return;
      if (data?.suggestedName) {
        setName(data.suggestedName);
        // Don't mark touched — that suppresses the "available" badge
        // until the user actually edits.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Debounced availability check. We don't fire on every keystroke;
  // a 300ms idle window keeps the server light and avoids "checking..."
  // flashing while the user is mid-type.
  const debounceRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setAvailability({ kind: 'idle' });
      return;
    }
    if (trimmed.length < 3) {
      setAvailability({ kind: 'unavailable', reason: 'too_short' });
      return;
    }
    if (trimmed.length > 30) {
      setAvailability({ kind: 'unavailable', reason: 'too_long' });
      return;
    }
    setAvailability({ kind: 'checking' });
    debounceRef.current = window.setTimeout(async () => {
      const data = await apiJson<{ available: boolean; reason?: Availability extends { reason: infer R } ? R : never }>(
        `/auth/displayname-available?name=${encodeURIComponent(trimmed)}`,
      );
      if (!data) {
        setAvailability({ kind: 'idle' });
        return;
      }
      if (data.available) {
        setAvailability({ kind: 'available' });
      } else {
        setAvailability({
          kind: 'unavailable',
          reason: (data.reason ?? 'in_use') as 'in_use',
        });
      }
    }, 300);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [name]);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitError(null);
    if (availability.kind !== 'available') return;
    setSubmitting(true);
    try {
      const r = await apiFetch('/auth/oauth/complete-signup', {
        method: 'POST',
        body: JSON.stringify({ displayName: name.trim() }),
      });
      if (r.ok) {
        // AuthContext lazily picks up via refresh on the home route,
        // but a hard nav is the simplest way to land them on the app
        // with a clean state.
        window.location.href = '/';
        return;
      }
      const body = await r.json().catch(() => ({}));
      const code = String(body.error ?? 'request_failed');
      setSubmitError(SUBMIT_ERROR_COPY[code] ?? code);
      // If the name was claimed in the race window, the check status
      // should flip to "in_use" too so the user sees both signals.
      if (code === 'displayName_in_use') {
        setAvailability({ kind: 'unavailable', reason: 'in_use' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ minHeight: '100dvh', background: v('paper'), color: v('ink') }}>
      <header style={{ padding: '18px 22px', borderBottom: `1px solid ${v('ruleSoft')}` }}>
        <a href="/" style={{ textDecoration: 'none', color: 'inherit', display: 'inline-block' }}>
          <Wordmark />
        </a>
      </header>
      <main style={{ maxWidth: 460, margin: '0 auto', padding: '40px 22px 60px' }}>
        <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: v('accent') }}>
          One more step
        </p>
        <h1 style={{ margin: '0 0 10px', fontSize: 28, fontWeight: 600, letterSpacing: '-0.01em' }}>
          Pick a display name
        </h1>
        <p style={{ margin: '0 0 20px', color: v('inkSoft'), fontSize: 14, lineHeight: 1.55 }}>
          This is the name other players see on your profile and on
          leaderboards. You can change it later from your account.
        </p>

        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, color: v('inkSoft') }}>Display name</span>
            <input
              type="text"
              value={name}
              onChange={e => { setName(e.target.value); setTouched(true); setSubmitError(null); }}
              autoFocus
              required
              minLength={3}
              maxLength={30}
              autoComplete="username"
              placeholder="3–30 characters"
              style={inputStyle(availability, touched)}
            />
            <AvailabilityHint availability={availability} touched={touched} />
          </label>

          {submitError && (
            <div style={{
              padding: '8px 12px',
              borderRadius: 8,
              background: v('paperDeep'),
              color: v('bad'),
              fontSize: 13,
              border: `1px solid ${v('bad')}`,
            }}>{submitError}</div>
          )}

          <div style={{ marginTop: 6 }}>
            <button
              type="submit"
              className="hm-primary"
              disabled={submitting || availability.kind !== 'available'}
              title={availability.kind !== 'available' ? 'Pick an available name first' : undefined}
            >
              {submitting ? 'Creating account…' : 'Finish sign up'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}

function AvailabilityHint({ availability, touched }: { availability: Availability; touched: boolean }) {
  // Don't render anything before the user has typed — keeps the page
  // calm on first paint.
  if (availability.kind === 'idle' || !touched) {
    return (
      <span style={{ fontSize: 11, color: v('inkMute') }}>
        Letters, numbers, and underscores. Visible on your profile.
      </span>
    );
  }
  if (availability.kind === 'checking') {
    return <span style={{ fontSize: 11, color: v('inkMute') }}>Checking…</span>;
  }
  if (availability.kind === 'available') {
    return (
      <span style={{ fontSize: 11, color: v('ok') }}>
        ✓ Available
      </span>
    );
  }
  return (
    <span style={{ fontSize: 11, color: v('bad') }}>
      {REASON_COPY[availability.reason] ?? availability.reason}
    </span>
  );
}

function inputStyle(availability: Availability, touched: boolean): React.CSSProperties {
  let borderColor = v('rule');
  if (touched) {
    if (availability.kind === 'available') borderColor = v('ok');
    else if (availability.kind === 'unavailable') borderColor = v('bad');
  }
  return {
    fontFamily: 'inherit',
    fontSize: 14,
    padding: '10px 12px',
    border: `1px solid ${borderColor}`,
    borderRadius: 8,
    background: v('paper'),
    color: v('ink'),
    outline: 'none',
    transition: 'border-color 0.15s',
  };
}
